import { Alert, Button } from 'react-bootstrap';
import { ConsentContext, ConsentFlows } from '../../context/ConsentContext';
import React, { useContext, useEffect, useState } from 'react';
import { contentfulPropTypes, onboardingPropTypes } from '../../propTypes';
import { flowSteps, recordFlowProgress } from '../../utils/recordFlowProgress';
import {
  registerMpanTrustedConsent,
  registrationMethods,
  validationCodes,
} from '../../api/SmartMeter';

import { Auth } from 'aws-amplify';
import CloseButton from '../../ui/Styled/CloseButton';
import LoopSpinner from '../../ui/LoopSpinner';
import moment from 'moment';
import { useNavigate } from 'react-router';
import withContentfulTag from '../../utils/withContentfulTag';

const ConnectingContainer = ({ contentful, loading, fetched }) => {
  const navigate = useNavigate();
  const {
    moveDate,
    mpan,
    termsAndConditions,
    lookedUpMpanHash,
    setMethod,
    paymentMethodId,
    hasOnboarded,
    hasRegisterMpanTrustedConsent,
    setHasRegisterMpanTrustedConsent,
    consentFlow,
  } = useContext(ConsentContext);
  const [showAlert, setShowAlert] = useState(false);
  const [alert, setAlert] = useState(undefined);
  const [isConnecting, setIsConnecting] = useState(true);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const successPath = `/${consentFlow}/success`;

  useEffect(() => {
    const asyncRecordFlowProgress = async () => {
      await recordFlowProgress(flowSteps.CONNECTING_TO_SMART_METER);
    };
    asyncRecordFlowProgress();
  }, []);

  useEffect(() => {
    if (hasOnboarded) {
      navigate(successPath);
    } else {
      Auth.currentAuthenticatedUser().then(async user => {
        const moveInDateMonth = moveDate ? moment(moveDate).format('MM') : '';
        const moveInDateYear = moveDate ? moment(moveDate).format('YYYY') : '';
        const addressId = user.attributes.address;
        const mpanAutoFilled = lookedUpMpanHash === mpan;
        const transactionReference = paymentMethodId;

        let trustedConsentResult;

        // Don't reattempt trusted consent if we have already tried
        if (!hasRegisterMpanTrustedConsent) {
          setHasRegisterMpanTrustedConsent(true);
          trustedConsentResult = await registerMpanTrustedConsent(
            mpan,
            moveInDateMonth,
            moveInDateYear,
            termsAndConditions,
            addressId,
            mpanAutoFilled,
            transactionReference,
            consentFlow === ConsentFlows.RECONFIRM,
          );
        }

        setIsConnecting(false);

        // if data to energy is good, we go to success or we go to cannot confirm
        if (
          hasRegisterMpanTrustedConsent ||
          trustedConsentResult?.status === 200
        ) {
          setMethod(registrationMethods.TRUSTED_CONSENT);
          navigate(successPath);
        } else {
          setHasRegisterMpanTrustedConsent(false);
          addAlert('danger', trustedConsentResult.message);
          setAlreadyRegistered(
            trustedConsentResult.code === validationCodes.ALREADY_REGISTERED,
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navigate,
    lookedUpMpanHash,
    moveDate,
    mpan,
    paymentMethodId,
    setMethod,
    termsAndConditions,
  ]);

  const addAlert = (variant, message) => {
    setShowAlert(true);
    setAlert({ variant, message });
  };

  const hideAlert = () => {
    setShowAlert(false);
  };

  return (
    !loading &&
    fetched && (
      <>
        <h1 className="mb-3">{contentful?.title}</h1>
        {isConnecting && (
          <>
            <div className="mb-3">
              <LoopSpinner />
            </div>
            <p>{contentful?.subtitle}</p>
          </>
        )}
        {showAlert && alert && (
          <div className="pt-4">
            <Alert variant={alert.variant} onClose={() => hideAlert()}>
              Something went wrong - {alert.message}
              <CloseButton onClick={() => hideAlert()} />
            </Alert>
          </div>
        )}
        {alert && alreadyRegistered && (
          <Button
            onClick={() => navigate(successPath)}
            className="px-4"
            variant="primary">
            Continue
          </Button>
        )}
      </>
    )
  );
};

ConnectingContainer.propTypes = {
  ...contentfulPropTypes,
  ...onboardingPropTypes,
};

export default withContentfulTag(
  ConnectingContainer,
  'web_app_onboarding_connecting_page',
);
